import api from './api';

const withStoreId = (storeId) => (storeId ? { _storeId: storeId } : undefined);

/**
 * Get availability for a date, team members and services.
 * @param {string} date - Date as YYYY-MM-DD
 * @param {string[]} teamMemberIds - Array of team member GUIDs (empty = any professional)
 * @param {number[]} selectedServices - Array of service IDs
 * @returns {Promise<{ date: string, timeSlots: { timeSlot: string, availableTeamMemberIds: string[] }[] }>}
 */
export const getAvailability = async (date, teamMemberIds, selectedServices, selectedServiceComboIds = []) => {
  const response = await api.post('/appointments/availability', {
    date,
    teamMemberIds: teamMemberIds || [],
    selectedServices: selectedServices || [],
    selectedServiceComboIds: selectedServiceComboIds || [],
  });
  return response.data;
};

/**
 * Create an appointment.
 * @param {object} payload - { date, serviceAssignments, detailsFromCustomer?, customerMembershipIds?, membershipPlanId? }
 * @returns {Promise<{ appointmentId: string }>}
 */
export const createAppointment = async (payload) => {
  const { storeId, ...body } = payload;
  const response = await api.post('/appointments', body, {
    ...(storeId ? { _storeId: storeId } : undefined),
  });
  return response.data;
};

export const createManagementAppointment = async (payload) => {
  const { storeId, ...body } = payload;
  const response = await api.post('/appointments/management', body, {
    _authContext: 'management',
    ...(storeId ? { _storeId: storeId } : undefined),
  });
  return response.data;
};

/**
 * Get customer's recent appointments.
 * @returns {Promise<Array>} Array of AppointmentView
 */
export const getMyAppointments = async (storeId) => {
  const response = await api.get('/appointments', {
    _authContext: 'customer',
    ...(storeId ? { _storeId: storeId } : undefined),
  });
  return response.data;
};

/**
 * Get management agenda for one or more team members in a date range.
 * @param {string[]} teamMemberIds - Team member GUIDs
 * @param {{ from: string, to: string }} dateRange - Date range as YYYY-MM-DD
 * @returns {Promise<{ appointments: Array, timeOffs: Array }>}
 */
export const getManagementAgendaAppointments = async (teamMemberIds, dateRange) => {
  const params = new URLSearchParams();
  (teamMemberIds || []).forEach((teamMemberId) => {
    params.append('teamMemberIds', teamMemberId);
  });
  params.append('dateRange.from', dateRange.from);
  params.append('dateRange.to', dateRange.to);

  const response = await api.get(`/appointments/agenda?${params.toString()}`, {
    _authContext: 'management',
  });

  return {
    appointments: response.data?.appointments ?? [],
    timeOffs: response.data?.timeOffs ?? [],
  };
};

/**
 * Get own management agenda in a date range.
 * @param {{ from: string, to: string }} dateRange - Date range as YYYY-MM-DD
 * @returns {Promise<{ appointments: Array, timeOffs: Array }>}
 */
export const getOwnManagementAgendaAppointments = async (dateRange) => {
  const params = new URLSearchParams();
  params.append('dateRange.from', dateRange.from);
  params.append('dateRange.to', dateRange.to);

  const response = await api.get(`/appointments/agenda/own?${params.toString()}`, {
    _authContext: 'management',
  });

  return {
    appointments: response.data?.appointments ?? [],
    timeOffs: response.data?.timeOffs ?? [],
  };
};

/**
 * Ask the server what actually happened to an off-site payment attempt. The checkout
 * return URL carries no outcome, so this is the only way to tell a declined payment
 * apart from one that is still settling.
 * @returns {Promise<{ appointmentId: string, outcome: string }>}
 */
export const getAppointmentCheckoutOutcome = async (sessionId) => {
  const response = await api.get('/appointments/checkout-outcome', {
    params: { sessionId },
    _authContext: 'customer',
  });
  return {
    appointmentId: response.data?.appointmentId ?? response.data?.AppointmentId ?? null,
    outcome: response.data?.outcome ?? response.data?.Outcome ?? 'Unknown',
    bookingReleased: Boolean(response.data?.bookingReleased ?? response.data?.BookingReleased),
  };
};

// The API's Language enum is serialized by name (PT, EN, ES).
export const toPolicyLanguage = (language) => {
  const code = String(language || '').slice(0, 2).toUpperCase();
  return ['PT', 'EN', 'ES'].includes(code) ? code : undefined;
};

const toAmount = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * @param {{ acceptedCancellationPolicy?: boolean, language?: string }} [options] Terms must be
 * accepted here when they were not already accepted while booking.
 */
export const createAppointmentCheckoutSession = async (appointmentId, options = {}) => {
  const response = await api.post(
    `/appointments/${appointmentId}/checkout-session`,
    {
      acceptedCancellationPolicy: Boolean(options.acceptedCancellationPolicy),
      policyLanguage: toPolicyLanguage(options.language),
    },
    { _authContext: 'customer' },
  );
  return {
    clientSecret: response.data?.clientSecret ?? response.data?.ClientSecret ?? null,
    // Direct charges live on the store's Stripe account; Stripe.js must be opened on it.
    stripeAccountId: response.data?.stripeAccountId ?? response.data?.StripeAccountId ?? null,
    sessionId: response.data?.sessionId ?? response.data?.SessionId ?? null,
  };
};

/**
 * Closes an unpaid booking hold. A confirmed appointment, or one whose payment is still
 * settling, is left as it is.
 * @returns {Promise<{ outcome: 'Released' | 'Confirmed' | 'Processing' }>}
 */
export const abandonAppointmentCheckout = async (appointmentId) => {
  const response = await api.post(`/appointments/${appointmentId}/abandon-checkout`, null, {
    _authContext: 'customer',
  });
  const outcome = response.data?.outcome ?? response.data?.Outcome ?? 'Released';
  return { outcome };
};

const normalizePaymentOptionQuote = (option) => (option ? {
  onlineAmount: toAmount(option.onlineAmount ?? option.OnlineAmount),
  inStoreAmount: toAmount(option.inStoreAmount ?? option.InStoreAmount),
  nonRefundableAmount: toAmount(option.nonRefundableAmount ?? option.NonRefundableAmount),
  nonRefundableBasis: option.nonRefundableBasis ?? option.NonRefundableBasis ?? 'None',
  policyText: option.policyText ?? option.PolicyText ?? '',
} : null);

export const normalizePrepaymentQuote = (data) => ({
  currency: data?.currency ?? data?.Currency ?? null,
  requiresPrepayment: Boolean(data?.requiresPrepayment ?? data?.RequiresPrepayment),
  allowsPayInFull: Boolean(data?.allowsPayInFull ?? data?.AllowsPayInFull),
  depositAmount: toAmount(data?.depositAmount ?? data?.DepositAmount),
  cancellationWindowHours: toAmount(data?.cancellationWindowHours ?? data?.CancellationWindowHours) || 2,
  minimum: normalizePaymentOptionQuote(data?.minimum ?? data?.Minimum),
  full: normalizePaymentOptionQuote(data?.full ?? data?.Full),
});

/**
 * What the booking summary shows before paying, worded by the server so the terms the customer
 * accepts are exactly the ones stored.
 * @param {{ storeId?: string, date: string, serviceAssignments: Array, coveredServiceIds?: number[], pendingMembershipEnrollment?: boolean, products?: Array<{ productId: string, quantity: number }>, language?: string }} payload
 */
export const getPrepaymentQuote = async ({ storeId, language, ...body }) => {
  const response = await api.post(
    '/appointments/prepayment-quote',
    { ...body, language: toPolicyLanguage(language) },
    { _authContext: 'customer', ...withStoreId(storeId) },
  );
  return normalizePrepaymentQuote(response.data);
};

/** The terms for paying an existing booking that was not paid while booking. */
export const getAppointmentCheckoutTerms = async (appointmentId, language) => {
  const response = await api.get(`/appointments/${appointmentId}/checkout-terms`, {
    params: { language: toPolicyLanguage(language) },
    _authContext: 'customer',
  });
  const data = response.data;
  return {
    alreadyAccepted: Boolean(data?.alreadyAccepted ?? data?.AlreadyAccepted),
    currency: data?.currency ?? data?.Currency ?? null,
    onlineAmount: toAmount(data?.onlineAmount ?? data?.OnlineAmount),
    nonRefundableAmount: toAmount(data?.nonRefundableAmount ?? data?.NonRefundableAmount),
    policyText: data?.policyText ?? data?.PolicyText ?? '',
  };
};

/** Exactly what the customer would get back, using the same rules the cancellation applies. */
export const getAppointmentCancellationPreview = async (appointmentId) => {
  const response = await api.get(`/appointments/${appointmentId}/cancellation-preview`, {
    _authContext: 'customer',
  });
  const data = response.data;
  return {
    canCancel: Boolean(data?.canCancel ?? data?.CanCancel),
    reasonCode: data?.reasonCode ?? data?.ReasonCode ?? null,
    isLateCancellation: Boolean(data?.isLateCancellation ?? data?.IsLateCancellation),
    amountPaid: toAmount(data?.amountPaid ?? data?.AmountPaid),
    refundAmount: toAmount(data?.refundAmount ?? data?.RefundAmount),
    nonRefundableAmount: toAmount(data?.nonRefundableAmount ?? data?.NonRefundableAmount),
    nonRefundableBasis: data?.nonRefundableBasis ?? data?.NonRefundableBasis ?? 'None',
    currency: data?.currency ?? data?.Currency ?? null,
    cancellationWindowHours: toAmount(data?.cancellationWindowHours ?? data?.CancellationWindowHours) || 2,
  };
};

export const getAppointmentProductUsage = async (storeId, appointmentId) => {
  const response = await api.get(`/appointments/${appointmentId}/product-usage`, {
    _authContext: 'management',
    ...withStoreId(storeId),
  });
  const lines = Array.isArray(response.data)
    ? response.data
    : (response.data?.lines ?? response.data?.products ?? response.data?.items ?? []);

  return {
    lines: (lines || []).map((line) => ({
      productId: line?.productId ?? line?.ProductId,
      name: line?.name ?? line?.productName ?? line?.Name ?? line?.ProductName ?? '',
      quantity: Number(line?.quantity ?? line?.Quantity) || 0,
      stockUnit: line?.stockUnit ?? line?.StockUnit ?? 'Unit',
      unitCost: line?.unitCost ?? line?.UnitCost ?? null,
      contentQuantity: Number(line?.contentQuantity ?? line?.ContentQuantity) || 1,
    })),
  };
};

export const updateAppointmentStatus = async (appointmentId, status, storeId, checkout = null) => {
  await api.patch(
    `/appointments/${appointmentId}/status`,
    {
      status,
      ...(checkout ? {
        paymentMethod: checkout.paymentMethod,
        amountReceived: checkout.amountReceived,
        payments: checkout.payments,
        customerProductLines: checkout.customerProductLines,
        staffProductLines: checkout.staffProductLines,
        customerProductSellerTeamMemberId: checkout.customerProductSellerTeamMemberId ?? null,
        staffProductSellerTeamMemberId: checkout.staffProductSellerTeamMemberId ?? null,
        ...(checkout.consumedProducts ? { consumedProducts: checkout.consumedProducts } : {}),
      } : {}),
    },
    {
      _authContext: 'management',
      ...withStoreId(storeId),
    },
  );
};

export const markAppointmentPaid = async (appointmentId, storeId, checkout) => {
  await api.post(`/appointments/${appointmentId}/mark-paid`, {
    paymentMethod: checkout?.paymentMethod ?? null,
    amountReceived: checkout?.amountReceived,
    payments: checkout?.payments,
    customerProductLines: checkout?.customerProductLines,
    staffProductLines: checkout?.staffProductLines,
    customerProductSellerTeamMemberId: checkout?.customerProductSellerTeamMemberId ?? null,
    staffProductSellerTeamMemberId: checkout?.staffProductSellerTeamMemberId ?? null,
  }, {
    _authContext: 'management',
    ...withStoreId(storeId),
  });
};

export const setAppointmentServicePrice = async (appointmentId, serviceId, unitPrice, storeId) => {
  await api.put(`/appointments/${appointmentId}/services/${serviceId}/price`, { unitPrice }, {
    _authContext: 'management',
    ...withStoreId(storeId),
  });
};

export const updateManagementAppointment = async (appointmentId, payload, storeId) => {
  await api.put(`/appointments/${appointmentId}/management`, payload, {
    _authContext: 'management',
    ...withStoreId(storeId),
  });
};

export const changeAppointmentToRegular = async (appointmentId, storeId) => {
  await api.post(`/appointments/${appointmentId}/change-to-regular`, null, {
    _authContext: 'management',
    ...withStoreId(storeId),
  });
};

export const confirmOnRequestAppointment = async (appointmentId, storeId, teamMemberId) => {
  await api.post(
    `/appointments/${appointmentId}/confirm`,
    teamMemberId ? { teamMemberId } : {},
    {
      _authContext: 'management',
      ...withStoreId(storeId),
    },
  );
};

export const declineOnRequestAppointment = async (appointmentId, storeId, teamMemberId) => {
  await api.post(
    `/appointments/${appointmentId}/decline`,
    teamMemberId ? { teamMemberId } : {},
    {
      _authContext: 'management',
      ...withStoreId(storeId),
    },
  );
};

export const deleteAppointment = async (appointmentId, storeId) => {
  await api.delete(`/appointments/${appointmentId}`, {
    _authContext: 'management',
    ...withStoreId(storeId),
  });
};

export const cancelMyAppointment = async (appointmentId) => {
  const response = await api.post(`/appointments/${appointmentId}/cancel`, null, {
    _authContext: 'customer',
  });
  const data = response?.data;
  return {
    isLateCancellation: Boolean(data?.isLateCancellation ?? data?.IsLateCancellation),
    refundIssued: Boolean(data?.refundIssued ?? data?.RefundIssued),
    refundPending: Boolean(data?.refundPending ?? data?.RefundPending),
    refundAmount: toAmount(data?.refundAmount ?? data?.RefundAmount),
    retainedAmount: toAmount(data?.retainedAmount ?? data?.RetainedAmount),
    currency: data?.currency ?? data?.Currency ?? null,
  };
};

const appointmentService = {
  getAvailability,
  createAppointment,
  createManagementAppointment,
  getMyAppointments,
  getManagementAgendaAppointments,
  getOwnManagementAgendaAppointments,
  getAppointmentCheckoutOutcome,
  createAppointmentCheckoutSession,
  abandonAppointmentCheckout,
  getPrepaymentQuote,
  getAppointmentCheckoutTerms,
  getAppointmentCancellationPreview,
  getAppointmentProductUsage,
  updateAppointmentStatus,
  markAppointmentPaid,
  setAppointmentServicePrice,
  updateManagementAppointment,
  changeAppointmentToRegular,
  confirmOnRequestAppointment,
  declineOnRequestAppointment,
  deleteAppointment,
  cancelMyAppointment,
};

export default appointmentService;
